/**
 * Recognise "Maximum call stack size exceeded" reports thrown by scripts that
 * something on the device injected into the page (content blockers, in-app
 * browsers, page recorders) rather than by our own bundle.
 *
 * Such frames come in two shapes:
 *
 *   - no file at all ("", "undefined", "null") — the script has no URL;
 *   - the page's own URL at a line past 1 — WebKit attributes injected
 *     scripts to the document. We serve every page as a single line of HTML,
 *     so a frame on line 190 of the document can't be our inline script.
 *
 * A real first-party overflow would leave at least one frame pointing at a
 * /_nuxt/ asset (or line 1 of the document), so it still reports.
 */

interface Frame {
  file?: string | null
  lineNumber?: number | null
}

const STACK_OVERFLOW = "Maximum call stack size exceeded"

const withoutQueryOrHash = (url: string) => url.split(/[?#]/)[0]

export function isInjectedStackOverflow(
  message: string,
  frames: Frame[],
  pageUrl: string,
): boolean {
  if (!message.includes(STACK_OVERFLOW) || frames.length === 0) return false

  const page = withoutQueryOrHash(pageUrl)

  return frames.every((f) => {
    const file = f.file ?? ""
    if (!file || file === "undefined" || file === "null") return true
    return withoutQueryOrHash(file) === page && (f.lineNumber ?? 0) > 1
  })
}
